import React from 'react';
import { Box, Typography } from '@mui/material';
import { Science as ScienceIcon, WarningAmber as WarningAmberIcon, AutoAwesome as SparkleIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { colors } from '../../../themes/typography';
import { getExperimentStatusColor } from '../../../themes/experimentColors';
import SlotTooltip from './SlotTooltip';

const ALERT_COLOR = '#ef4444';

// Experiment mode marks on a lane: picked to come in (green), recommended to come in (star), a queued lane that
// can't be picked as the incoming product (faded), or a lane of the suggestion open in the panel (its purple)
const PICKED = '#15803d';
const RECOMMENDED = '#16a34a';
const SUGGESTION = getExperimentStatusColor('manual_review');

const EnhancedSlot = ({
  slot, heatmapBg, heatText, isSelected, onInspect, onHover, onLeave, hideTooltip = false, marker = null, cellSize, fontSize,
  metricFontSize = '11px', heatmapMode, heatmapValue, absoluteValue, period = 30,
}) => {
  const { t } = useTranslation();
  const isLarge = cellSize >= 110;
  // A planogram fitted to the window can make lanes short: then the metrics row goes (the tooltip keeps it)
  const isCompact = cellSize < 66;
  const [tooltip, setTooltip] = React.useState(null);
  const enterTimer = React.useRef(null);

  const handleMouseEnter = React.useCallback((e) => {
    onHover?.(slot, e.currentTarget);
    if (hideTooltip) return;
    const rect = e.currentTarget.getBoundingClientRect();
    enterTimer.current = setTimeout(() => {
      setTooltip({ x: rect.left + rect.width / 2, y: rect.top });
    }, 200);
  }, [onHover, slot, hideTooltip]);

  const handleMouseLeave = React.useCallback(() => {
    clearTimeout(enterTimer.current);
    setTooltip(null);
    onLeave?.(slot);
  }, [onLeave, slot]);

  if (!slot || !slot.name) {
    return (
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.background.subtle,
        borderRadius: '8px',
        border: `1px solid ${colors.border.light}`,
        height: '100%',
        opacity: 0.4
      }}>
        <Typography sx={{ fontSize, color: colors.text.muted }}>-</Typography>
      </Box>
    );
  }

  const hasExperiment = Boolean(slot.experiment) && slot.experiment.status !== 'rejected';
  const hasSplitExperiments = Array.isArray(slot.experiments) && slot.experiments.length > 1;
  // Open technical alert (no sales for N weeks) with no experiment yet — the experiment pill wins otherwise
  const hasAlert = Boolean(slot.alert) && !hasExperiment;
  const tier = hasExperiment ? 'experiment' : (hasAlert ? 'alert' : 'normal');

  // Detect replacement/lane-expansion experiment where planogram still shows original product (swap not done)
  const needsSwapWarning = hasExperiment
    && ['reemplazo_producto', 'ampliar_carril'].includes(slot.experiment.experimentType)
    && slot.experiment.status === 'running'
    && slot.experiment.isSwapped === false;

  const isUnitsMode = heatmapMode === 'units';
  const isHeatCell = isUnitsMode && Boolean(heatmapBg);
  // Sales heatmap: the card color is the data, text flips to white on the darkest cells
  const ink = !isHeatCell
    ? { primary: colors.text.primary, secondary: colors.text.secondary, muted: colors.text.muted }
    : heatText === 'light'
    ? { primary: '#fff', secondary: 'rgba(255,255,255,0.85)', muted: 'rgba(255,255,255,0.85)' }
    : { primary: colors.text.primary, secondary: colors.text.secondary, muted: 'rgba(28,25,23,0.65)' };

  const borderColor = marker === 'picked' ? PICKED
    : marker === 'suggestion' ? SUGGESTION
    : isSelected ? colors.primary.main
    : marker === 'recommended' ? RECOMMENDED
    : (isHeatCell ? 'rgba(194,65,12,0.18)' : colors.border.light);
  const borderWidth = isSelected || marker === 'picked' || marker === 'recommended' || marker === 'suggestion' ? 2 : 1;

  // Top-edge bar color: experiment status, else technical alert
  const topBarColor = hasExperiment ? getExperimentStatusColor(slot.experiment.status) : (hasAlert ? ALERT_COLOR : null);

  // Performance health indicator (traffic light): colored symbol
  const performanceIndicator = (() => {
    const delta = slot.salesDeltaPercentage;
    if (delta === null || delta === undefined) return null;
    if (delta > 5) return { color: colors.status.success, symbol: '▲' };
    if (delta > -15) return { color: colors.text.secondary, symbol: '—' };
    return { color: '#ef4444', symbol: '▼' };
  })();

  const isCategoryMode = heatmapMode === 'category';
  const isProductsMode = heatmapMode === 'products' || heatmapMode === 'none' || isUnitsMode;

  const pillSx = (color) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '1px',
    fontSize: isCompact ? '9px' : '10px',
    fontWeight: 700,
    color,
    backgroundColor: isUnitsMode ? '#fff' : `${color}18`,
    border: `1px solid ${color}30`,
    px: '3px',
    py: '0px',
    borderRadius: '3px',
    lineHeight: isCompact ? 1 : 1.2,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
    minWidth: 0,
    flexShrink: 1,
  });

  // Unified status pill — experiment status, else technical alert; only color + text change
  const renderStatusPill = () => {
    if (hasAlert) {
      const alertLabel = t('machines_alertNoSalesShort', { weeks: slot.alert.weeksSince });
      return (
        <Box aria-label={`${t('machines_alertTechnical')}: ${alertLabel}`} sx={pillSx(ALERT_COLOR)}>
          <WarningAmberIcon sx={{ fontSize: isCompact ? 10 : 11, color: ALERT_COLOR }} />
          {alertLabel}
        </Box>
      );
    }
    if (!hasExperiment) return null;
    const status = slot.experiment.status;
    const statusColor = getExperimentStatusColor(status);
    const isManualReview = status === 'manual_review';
    const statusLabel = isManualReview ? t('machines_aiSuggestion') : t(status);
    const StatusIcon = isManualReview ? SparkleIcon : ScienceIcon;
    return (
      <Box
        aria-label={`${t('machines_experiment')}: ${statusLabel}${needsSwapWarning ? ` (${t('machines_swapPending', 'Swap pending')})` : ''}`}
        sx={pillSx(statusColor)}
      >
        <StatusIcon sx={{ fontSize: isCompact ? 10 : 11, color: statusColor }} />
        {statusLabel}
        {hasSplitExperiments && (
          <Box component="span" sx={{ fontSize: '9px', fontWeight: 800, ml: '2px' }}>×{slot.experiments.length}</Box>
        )}
        {needsSwapWarning && (
          <WarningAmberIcon sx={{ fontSize: 10, color: '#F59E0B' }} />
        )}
      </Box>
    );
  };

  // Format metrics for Products mode bottom row
  const salesLabel = slot.productAvgSales > 0 ? `${Number(slot.productAvgSales).toFixed(1)}u/d` : null;
  const marginLabel = slot.productMargin > 0 ? `€${Number(slot.productMargin).toFixed(2)}` : null;

  const renderContent = () => {
    if (isProductsMode) {
      // Products mode: name-centric with embedded metrics row
      return (
        <>
          {/* Header: status pill + slot ID (right-aligned) */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
            {renderStatusPill()}
            <Typography sx={{
              fontSize: metricFontSize,
              fontWeight: 600,
              color: ink.muted,
              lineHeight: 1,
              marginLeft: 'auto',
              flexShrink: 0
            }}>
              {slot.selection_id || `${slot.row}-${slot.column}`}
            </Typography>
          </Box>

          {/* Center: product name (dominant) */}
          <Typography sx={{
            fontSize,
            fontWeight: 500,
            color: ink.primary,
            lineHeight: isCompact ? 1.15 : 1.25,
            textAlign: 'center',
            wordBreak: 'break-word',
            flex: 1,
            // Exactly the clamped lines: a taller box lets the top of the next line peek out. A short lane with a status
            // pill only has room for one
            maxHeight: isCompact ? (tier === 'normal' ? '2.3em' : '1.15em') : '2.5em',
            display: '-webkit-box',
            WebkitLineClamp: isCompact && tier !== 'normal' ? 1 : 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            alignContent: 'center'
          }}>
            {slot.name}
          </Typography>

          {/* Bottom: nothing on the sales heatmap — the colour is the value, the tooltip says it */}
          {isCompact || isUnitsMode ? null : (
          /* Bottom: metrics row (sales, margin, trend) */
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '2px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', overflow: 'hidden' }}>
              {salesLabel && (
                <Typography sx={{
                  fontSize: metricFontSize,
                  color: colors.text.secondary,
                  lineHeight: 1,
                  whiteSpace: 'nowrap'
                }}>
                  {salesLabel}
                </Typography>
              )}
              {marginLabel && (
                <Typography sx={{
                  fontSize: metricFontSize,
                  color: colors.text.secondary,
                  lineHeight: 1,
                  whiteSpace: 'nowrap'
                }}>
                  {marginLabel}
                </Typography>
              )}
            </Box>
            {performanceIndicator && (
              <Typography sx={{
                fontSize: isLarge ? '12px' : '11px',
                fontWeight: 700,
                color: performanceIndicator.color,
                lineHeight: 1,
                flexShrink: 0
              }}>
                {performanceIndicator.symbol}
              </Typography>
            )}
          </Box>
          )}
        </>
      );
    }

    // Category mode: name-first, colored background, no metrics
    return (
      <>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
          {renderStatusPill()}
          <Typography sx={{
            fontSize: metricFontSize,
            fontWeight: 600,
            color: colors.text.muted,
            lineHeight: 1,
            marginLeft: 'auto',
            flexShrink: 0
          }}>
            {slot.selection_id || `${slot.row}-${slot.column}`}
          </Typography>
        </Box>

        <Typography sx={{
          fontSize,
          fontWeight: 500,
          color: colors.text.primary,
          lineHeight: 1.25,
          textAlign: 'center',
          wordBreak: 'break-word',
          flex: 1,
          display: '-webkit-box',
          WebkitLineClamp: isLarge ? 3 : 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          alignContent: 'center'
        }}>
          {slot.name}
        </Typography>

        <Box sx={{ height: 12 }} />
      </>
    );
  };

  return (
    <>
      {tooltip && (
        <Box sx={{
          position: 'fixed',
          left: tooltip.x,
          top: tooltip.y,
          transform: 'translate(-50%, -100%)',
          mt: '-8px',
          zIndex: 1500,
          pointerEvents: 'none',
          width: 'max-content',
          maxWidth: '280px',
        }}>
          <Box sx={{
            backgroundColor: 'rgba(30,30,30,0.95)',
            color: '#fff',
            borderRadius: '8px',
            px: 1.5,
            py: 1,
            fontSize: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          }}>
            <SlotTooltip slot={slot} period={period} unitsPerDay={isHeatCell ? heatmapValue : undefined} />
          </Box>
        </Box>
      )}
      <Box
        onClick={() => onInspect?.(slot)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        p: isLarge ? '8px' : isCompact ? '3px 6px' : '6px',
        pt: topBarColor ? (isLarge ? '11px' : isCompact ? '6px' : '9px') : undefined,
        height: '100%',
        backgroundColor: tier === 'experiment' && !heatmapBg
          ? `${getExperimentStatusColor(slot.experiment.status)}12`
          : tier === 'alert' && !heatmapBg
          ? `${ALERT_COLOR}12`
          : heatmapBg || colors.background.surface,
        border: `${borderWidth}px ${marker === 'recommended' && !isSelected ? 'dashed' : 'solid'} ${borderColor}`,
        borderRadius: '8px',
        cursor: marker === 'dimmed' ? 'not-allowed' : 'pointer',
        opacity: marker === 'dimmed' ? 0.45 : 1,
        transition: 'box-shadow 150ms ease-out',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: marker === 'picked' ? `0 0 0 1px ${PICKED}`
          : marker === 'suggestion' ? `0 0 0 1px ${SUGGESTION}`
          : isSelected ? `0 0 0 1px ${colors.primary.main}` : 'none',
        '@media (prefers-reduced-motion: no-preference)': {
          transition: 'all 150ms ease-out'
        },
        // While picking, a lane that can come in turns green under the pointer
        '&:hover': marker === 'pickable' || marker === 'recommended' ? {
          borderColor: PICKED,
          boxShadow: `0 0 0 1px ${PICKED}, 0 2px 8px ${colors.shadow.soft}`,
          zIndex: 1,
        } : {
          boxShadow: `0 2px 8px ${colors.shadow.soft}`,
          zIndex: 1,
        },
        '&:focus-visible': {
          outline: `2px solid ${colors.primary.main}`,
          outlineOffset: '2px'
        }
      }}
    >
      {/* Top-edge bar: experiment status color */}
      {topBarColor && (
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          backgroundColor: topBarColor,
          borderRadius: '7px 7px 0 0',
          zIndex: 2,
        }} />
      )}

      {marker === 'recommended' && (
        <Box component="span" aria-hidden sx={{ position: 'absolute', bottom: 1, right: 3, fontSize: '11px', lineHeight: 1, color: RECOMMENDED }}>★</Box>
      )}

      {renderContent()}
    </Box>
    </>
  );
};

export default React.memo(EnhancedSlot);
